import { useRef, useEffect, KeyboardEventHandler } from 'react';
import pxToRem from '../../../utils/pxToRem';

import { useCalendarContext } from '../../context';
import { MONTHS, NAVIGATION_STATES, KEYS } from '../../constants';
import {
  parseDate,
  getOffset,
  isDateInInterval,
  isDateValid,
  isArrow,
  getDateParts,
  getFirstSelectedDate,
} from '../../helpers';
import { DateButton } from '../../DateButton/DateButton';

type Props = {
  'data-date': string;
};

export const Month = (props: Props) => {
  const ref = useRef<HTMLButtonElement>(null);
  const {
    visible,
    currentDate,
    focusedDate,
    selectedDate,
    selectionType,
    calendarType,
    minDate,
    maxDate,
    selectionDateType,
    structuredDisabledMonths,
    onSelectedDateChange,
    onInnerDateChange,
    setCalendarType,
  } = useCalendarContext();

  const date = props['data-date'];
  const { day, month, year } = getDateParts(date);
  const originalMinDate = minDate && isDateValid(minDate) && new Date(minDate);
  const originalMaxDate = maxDate && isDateValid(maxDate) && new Date(maxDate);
  const isDateBeforeMinDate =
    originalMinDate &&
    (originalMinDate.getFullYear() > year ||
      (originalMinDate.getFullYear() === year &&
        originalMinDate.getMonth() > month));
  const isDateAfterMaxDate =
    originalMaxDate &&
    (originalMaxDate.getFullYear() < year ||
      (originalMaxDate.getFullYear() === year &&
        originalMaxDate.getMonth() < month));
  const isDisabled =
    isDateBeforeMinDate ||
    isDateAfterMaxDate ||
    (structuredDisabledMonths[year] &&
      structuredDisabledMonths[year].includes(month + 1));
  const originalCurrentDate = new Date(currentDate);
  const isCurrentMonth =
    originalCurrentDate.getMonth() === month &&
    originalCurrentDate.getFullYear() === year;

  let startSelected = false;
  let endSelected = false;
  let tabIndex = -1;
  let isInInterval = false;
  if (selectionType === 'day' && selectedDate) {
    const isSelected = selectedDate.some((item) => {
      const originalItem = new Date(item);
      return (
        originalItem.getFullYear() === year && originalItem.getMonth() === month
      );
    });
    if (isSelected) {
      startSelected = true;
      endSelected = true;
    }
  } else if (selectedDate) {
    const startDate = new Date(selectedDate[0]);
    const endDate = new Date(selectedDate[1]);
    const parsedStartMonth = parseDate(
      new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    );
    const parsedEndMonth = parseDate(
      new Date(endDate.getFullYear(), endDate.getMonth(), 1)
    );
    const parsedDate = parseDate(new Date(year, month, 1));
    startSelected = parsedStartMonth === parsedDate;
    endSelected = parsedEndMonth === parsedDate;
    if (
      !(startSelected || endSelected) &&
      selectionType === 'interval' &&
      selectedDate.length === 2
    ) {
      isInInterval = isDateInInterval(selectedDate[0], selectedDate[1], date);
    }
  }
  if (isCurrentMonth) {
    tabIndex = 0;
  }

  const handleButtonKeydown: KeyboardEventHandler = (event) => {
    const { key } = event;
    const isHome = key === KEYS['HOME'];
    const isEnd = key === KEYS['END'];
    const isPageUp = key === KEYS['PAGE_UP'];
    const isPageDown = key === KEYS['PAGE_DOWN'];
    if (isArrow(key) || isHome || isEnd || isPageUp || isPageDown) {
      event.preventDefault();
      let neighbouringMonth = month;
      let neighbouringYear = year;
      if (isArrow(key)) {
        neighbouringMonth = month + getOffset('months', key);
      } else if (isHome) {
        neighbouringMonth = 0;
      } else if (isEnd) {
        neighbouringMonth = MONTHS.length - 1;
      } else if (isPageUp) {
        neighbouringYear = year - 1;
      } else if (isPageDown) {
        neighbouringYear = year + 1;
      }
      const newDate = new Date(neighbouringYear, neighbouringMonth, day);
      const newCurrentDate = parseDate(newDate);
      onInnerDateChange({ date: newCurrentDate, updateFocus: true });
    }
  };

  const handleClick = () => {
    if (startSelected || endSelected) {
      if (selectionType === 'interval' && endSelected) {
        onInnerDateChange({ date: selectedDate![1], updateFocus: true });
      } else {
        const firstSelectedDate = getFirstSelectedDate(
          date,
          selectedDate!,
          'month'
        );
        onInnerDateChange({ date: firstSelectedDate, updateFocus: true });
      }
    } else {
      onInnerDateChange({ date, updateFocus: true });
    }
    if (selectionDateType === 'month') {
      onSelectedDateChange(date);
    } else {
      const previousStateIndex = NAVIGATION_STATES.indexOf(calendarType) - 1;
      setCalendarType(NAVIGATION_STATES[previousStateIndex]);
    }
  };

  useEffect(
    function updateMonthFocus() {
      const isCurrentMonthAlreadyFocused = ref.current?.isEqualNode(
        document.activeElement
      );
      const originalFocusedDate = focusedDate
        ? new Date(focusedDate)
        : undefined;
      const isFocusedMonth =
        originalFocusedDate?.getMonth() === month &&
        originalFocusedDate?.getFullYear() === year;
      if (!isCurrentMonthAlreadyFocused && isFocusedMonth && visible) {
        ref.current?.focus();
      }
    },
    [focusedDate, date, visible]
  );

  return (
    <DateButton
      ref={ref}
      disabled={isDisabled}
      selected={startSelected || endSelected}
      tabIndex={tabIndex}
      isInInterval={isInInterval}
      onClick={handleClick}
      onKeyDown={handleButtonKeydown}
      styles={{
        pt: pxToRem(21),
        pb: pxToRem(22),
      }}
      {...props}
    >
      {MONTHS[month]}
    </DateButton>
  );
};
